import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Optional, RendererFactory2 } from '@angular/core';
import { Color, Crop, PhotoInfo, PhotoInfoRef } from '@dreambooks/core';
import { normalizePhotoSizeDimensions, PHOTO_VARIANT_MAX_DIM } from '../../../core/utils/photo-size-dimensions';
import { COMPOSITION_PLACEHOLDER_PREFIX, generatePlaceholderSvg } from '../utils/composition-utils';
import {
  DreambooksRendererConfig,
  PROJECT_PHOTOS,
  SHARED_PHOTOS_BLOB,
  RendererService,
  RendererFontsService
} from '@dreambooks/renderer';
import { from, Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { PhotoBlobCacheService } from './photo-blob-cache.service';

/**
 * Extended RendererService that adds caching and caps image loading at _b size.
 *
 * - Caches processed photo data URLs to avoid re-loading and re-processing
 *   the same images when switching between pages.
 * - Sanitizes photo sources to keep only bounded variants (_ts/_t/_s/_m/_b/_l).
 * - Blocks _e/original fallback in renderer source selection.
 * - Keeps native nearest-size behavior so manual pagination can pick smaller
 *   variants when elements are smaller.
 */
@Injectable()
export class CachedRendererService extends RendererService {
  private readonly limitToSizeB = true;
  /**
   * Variant URLs that actually failed to load THIS session (loadWithSizeFallback
   * removed them). The ladder rebuild must not resurrect these — but a fresh
   * session retries them, so a transient 5xx never becomes permanent damage.
   */
  private readonly failedVariantUrls = new Set<string>();
  // _l tops out at ~1500 px on the long edge; keep the same headroom the old
  // 900 cap gave the 800 px _b variant.
  private readonly MAX_BOUNDED_EDGE = 1600;
  private presentationScale = 1;

  constructor(
    config: DreambooksRendererConfig,
    @Inject(PROJECT_PHOTOS) projectPhotosMap: Map<string, PhotoInfo>,
    @Optional() @Inject(SHARED_PHOTOS_BLOB) sharedPhotosBlobMap: Map<string, string>,
    @Inject(DOCUMENT) mDocument: any,
    rendererFactory: RendererFactory2,
    fontsService: RendererFontsService,
    private photoBlobCache: PhotoBlobCacheService
  ) {
    super(config, projectPhotosMap, sharedPhotosBlobMap, mDocument, rendererFactory, fontsService);
    console.log('[CachedRendererService] Initialized with caching support');
  }

  /**
   * No-op — kept for API compatibility with callers that set the presentation scale.
   */
  setPresentationScale(scale: number): void {
    this.presentationScale = this.normalizeScale(scale);
  }

  /**
   * Override loadImageData to add caching and enforce bounded source selection.
   * This keeps the normal nearest-size behavior from RendererService while ensuring
   * original/_l/_e variants are excluded from photo size resolution.
   */
  override loadImageData(
    photoRef: PhotoInfoRef,
    width?: number,
    height?: number,
    crop?: Crop,
    color?: Color
  ): Observable<string> {
    if (!photoRef) {
      return super.loadImageData(photoRef, width, height, crop, color);
    }

    // Fast path: composition placeholder photos return a sized SVG immediately.
    // This avoids the async Image loading pipeline and prevents pageRendered timeout.
    // SVG is generated at the element's exact dimensions so borders/icon never deform.
    if (photoRef.md5?.startsWith(COMPOSITION_PLACEHOLDER_PREFIX)) {
      const pw = width || 300;
      const ph = height || 200;
      return of(generatePlaceholderSvg(pw, ph));
    }

    this.prepareLimitedPhotoSizes(photoRef.md5);

    const request = this.getScaledRequest(width, height, crop);

    // Can't cache without proper dimensions (library fallback path)
    if (!request.width || !request.height) {
      return super.loadImageData(photoRef, request.width, request.height, request.crop, color);
    }

    // Cache key: md5 + dimensions + crop + color
    const cacheKey = this.photoBlobCache.generateCacheKey(
      photoRef,
      request.width,
      request.height,
      request.crop,
      color
    );

    // Check if we have a cached version
    const cachedPromise = this.photoBlobCache.get(cacheKey);
    if (cachedPromise) {
      // Convert cached Promise to Observable
      return new Observable(observer => {
        cachedPromise
          .then(dataUrl => {
            observer.next(dataUrl);
            observer.complete();
          })
          .catch(err => observer.error(err));
      });
    }

    console.log(`[CachedRendererService] Cache MISS for ${cacheKey.substring(0, 30)}...`,
      `crop: ${request.crop ? `${Math.round(request.crop.width)}x${Math.round(request.crop.height)}` : 'none'}`,
      `element: ${request.width}x${request.height}`,
      `presentationScale: ${this.presentationScale.toFixed(3)}`);

    // Create a promise to store in cache immediately (to handle concurrent requests)
    let resolvePromise: (value: string) => void;
    let rejectPromise: (reason: any) => void;
    const dataUrlPromise = new Promise<string>((resolve, reject) => {
      resolvePromise = resolve;
      rejectPromise = reject;
    });

    // Store in cache immediately to handle concurrent requests for the same image
    this.photoBlobCache.set(cacheKey, dataUrlPromise);

    // Fine in-container rotation (crop.fineRotation, ±45°): the lib's bake
    // only understands 90° steps, so arbitrary angles are applied as a
    // post-step over a BASE bake (same request WITHOUT fineRotation) that is
    // cached under its own key. During dial drags every angle change hits
    // the cached base — only the cheap rotate+encode runs per step, instead
    // of re-fetching/decoding/PNG-encoding the whole pipeline each frame.
    const fineRotation = Number((request.crop as any)?.fineRotation) || 0;
    if (fineRotation) {
      return this.loadFineRotationBase(photoRef, request, color).pipe(
        switchMap(baseUrl => this.applyFineRotation(baseUrl, fineRotation)),
        tap({
          next: (dataUrl) => resolvePromise(dataUrl),
          error: (err) => {
            this.photoBlobCache.clearPhoto(photoRef.md5);
            rejectPromise(err);
          }
        })
      );
    }

    // Load with retry: if a size variant returns 403/404, remove it and retry
    // with the next smaller size. This handles cliparts where the server only
    // created _t and _s but not _m or _b (image was too small).
    return this.loadWithSizeFallback(photoRef, request.width, request.height, request.crop, color).pipe(
      tap({
        next: (dataUrl) => {
          resolvePromise(dataUrl);
        },
        error: (err) => {
          // Remove failed cache entry so future requests can retry
          this.photoBlobCache.clearPhoto(photoRef.md5);
          rejectPromise(err);
        }
      })
    );
  }

  /**
   * Rotate the baked photo by an arbitrary fine angle (degrees) INSIDE the
   * same canvas: the image is rotated around its center and scaled up by the
   * minimal cover factor k(θ) = cos|θ| + sin|θ|·max(W/H, H/W) so the canvas
   * stays fully covered (no empty corners). Canvas dimensions are unchanged,
   * so the crop geometry the SVG applies afterwards remains valid.
   */
  /** Single-entry decode memo for the fine-rotation base image — during a
   *  dial drag every step reuses the same decoded HTMLImageElement. */
  private fineRotationBase: { url: string; img: HTMLImageElement } | null = null;

  /**
   * Resolve the BASE bake for a fine-rotated request (same request WITHOUT
   * the fine angle), cached under its own key. Crop dims are quantized for
   * the base: they only drive size-variant selection and change with every
   * dial step (the crop box is the rotated bounding box) — without
   * bucketing the base cache would miss on every angle.
   */
  private loadFineRotationBase(
    photoRef: PhotoInfoRef,
    request: { width?: number; height?: number; crop?: Crop },
    color?: Color
  ): Observable<string> {
    const baseCrop = this.clonePhotoLike(request.crop) as Crop;
    delete (baseCrop as any).fineRotation;
    baseCrop.width = Math.ceil((baseCrop.width || 0) / 256) * 256;
    baseCrop.height = Math.ceil((baseCrop.height || 0) / 256) * 256;
    const baseKey = this.photoBlobCache.generateCacheKey(photoRef, request.width || 0, request.height || 0, baseCrop, color);

    const cachedBase = this.photoBlobCache.get(baseKey);
    if (cachedBase) {
      return from(cachedBase);
    }

    let resolveBase!: (value: string) => void;
    let rejectBase!: (reason: any) => void;
    const basePromise = new Promise<string>((resolve, reject) => {
      resolveBase = resolve;
      rejectBase = reject;
    });
    this.photoBlobCache.set(baseKey, basePromise);
    return this.loadWithSizeFallback(photoRef, request.width, request.height, baseCrop, color).pipe(
      tap({
        next: (dataUrl) => resolveBase(dataUrl),
        error: (err) => {
          this.photoBlobCache.clearPhoto(photoRef.md5);
          rejectBase(err);
        }
      })
    );
  }

  private applyFineRotation(dataUrl: string, fineRotation: number | undefined): Observable<string> {
    const angle = Number(fineRotation) || 0;
    if (!angle || !dataUrl) return of(dataUrl);

    const rotate = (img: HTMLImageElement, observer: { next: (v: string) => void; complete: () => void }): void => {
      try {
        const w = img.width;
        const h = img.height;
        const rad = (angle * Math.PI) / 180;
        const cos = Math.abs(Math.cos(rad));
        const sin = Math.abs(Math.sin(rad));

        // Canvas = the rotated image's BOUNDING BOX — the FULL photo stays
        // available, so the standard crop pipeline (the editor mirrors these
        // dims into crop.bitmapWidth/Height while an angle is active) gives
        // full-range pan/zoom across the whole rotated image, matching
        // reference tools. Corners outside the rotated rect are filled white
        // (JPEG has no alpha; white matches the page in the common case).
        const bw = Math.max(1, Math.round(w * cos + h * sin));
        const bh = Math.max(1, Math.round(w * sin + h * cos));
        const canvas = document.createElement('canvas');
        canvas.width = bw;
        canvas.height = bh;
        const ctx = canvas.getContext('2d');
        if (!ctx) { observer.next(dataUrl); observer.complete(); return; }

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, bw, bh);
        ctx.translate(bw / 2, bh / 2);
        ctx.rotate(rad);
        ctx.drawImage(img, -w / 2, -h / 2);

        // JPEG: photos are opaque and the white fill removes any alpha need;
        // encoding is several times faster than PNG — that's what keeps the
        // live dial drag fluid.
        observer.next(canvas.toDataURL('image/jpeg', 0.9));
        observer.complete();
      } catch {
        // Tainted canvas or draw failure — fall back to the unrotated bake
        observer.next(dataUrl);
        observer.complete();
      }
    };

    return new Observable<string>(observer => {
      const memo = this.fineRotationBase;
      if (memo && memo.url === dataUrl) {
        rotate(memo.img, observer);
        return;
      }
      const img = new Image();
      img.onload = () => {
        this.fineRotationBase = { url: dataUrl, img };
        rotate(img, observer);
      };
      img.onerror = () => { observer.next(dataUrl); observer.complete(); };
      img.src = dataUrl;
    });
  }

  /**
   * Load image data with automatic fallback to smaller sizes on failure.
   * When a size variant (e.g., _m) returns 403/404, removes it from the photo's
   * sizes array and retries — the renderer picks the next smaller available size.
   */
  private loadWithSizeFallback(
    photoRef: PhotoInfoRef,
    width?: number,
    height?: number,
    crop?: Crop,
    color?: Color,
    attempt = 0
  ): Observable<string> {
    return super.loadImageData(photoRef, width, height, crop, color).pipe(
      catchError((err) => {
        const projectPhotosMap = (this as any).projectPhotosMap as Map<string, any> | undefined;
        const photo = projectPhotosMap?.get(photoRef.md5);
        console.warn('[CachedRenderer] loadImageData FAILED:', {
          md5: photoRef.md5,
          attempt,
          inPhotosMap: !!photo,
          sizesLeft: photo?.sizes?.length || 0,
          url: photo?.url,
          error: err?.message || err
        });
        if (!photo?.sizes?.length || attempt >= 4) {
          // No more sizes to try — return empty to keep whatever was visible
          console.warn('[CachedRenderer] loadImageData giving up — returning EMPTY (renders blank) for', photoRef.md5);
          return of('');
        }

        // Remove the largest size (most likely the one that failed) and retry
        const sorted = [...photo.sizes].sort(
          (a: any, b: any) => ((b.width || 0) * (b.height || 0)) - ((a.width || 0) * (a.height || 0))
        );
        const removed = sorted[0];
        const remaining = sorted.slice(1);

        // NOTE: the removed-largest entry is a GUESS about which variant
        // failed (the actual failing URL isn't surfaced by the renderer), so
        // it is deliberately NOT added to failedVariantUrls — only preflight
        // results (direct evidence) feed the blacklist. The ladder heal
        // re-verifies removed variants before ever re-adding them.
        console.log(`[CachedRenderer] Size fallback: removed ${this.getSizeVariant(removed) || 'unknown'} (${removed?.url?.split('/').pop()}), ${remaining.length} sizes left`);

        // Update the photo in the map with reduced sizes
        const updatedPhoto = this.clonePhotoLike(photo);
        updatedPhoto.sizes = remaining;
        // Update the main URL to the next best available
        if (remaining.length > 0) {
          updatedPhoto.url = remaining[0].url;
        }
        projectPhotosMap?.set(photoRef.md5, updatedPhoto);

        return this.loadWithSizeFallback(photoRef, width, height, crop, color, attempt + 1);
      })
    );
  }

  /**
   * Clear all cached images
   */
  clearCache(): void {
    this.photoBlobCache.clear();
  }

  /**
   * Clear cache for a specific photo (all variants)
   */
  clearPhotoCache(md5: string): void {
    this.photoBlobCache.clearPhoto(md5);
  }

  /**
   * Restrict photo sizes to bounded editor variants (_ts/_t/_s/_m/_b/_l) and
   * remove _e/original candidates from size selection.
   */
  private prepareLimitedPhotoSizes(md5: string): void {
    if (!this.limitToSizeB || !md5) return;

    // Skip composition placeholder photos — they use a data-URL thumbnail that
    // must not be moved to the main url field (the renderer appends ?cacheKey to urls).
    if (md5.startsWith(COMPOSITION_PLACEHOLDER_PREFIX)) return;

    const projectPhotosMap = (this as any).projectPhotosMap as Map<string, any> | undefined;
    const photo = projectPhotosMap?.get(md5);
    if (!photo) return;

    const allSizes = Array.isArray(photo.sizes)
      ? photo.sizes.filter((size: any) => !!size?.url)
      : [];

    // Repair size dimensions (missing 0×0 dims from recovery writebacks, or
    // inflated dims where every variant claims the ORIGINAL photo size — an
    // old persistence bug): PhotoUtil.getNearestSize ranks by AREA, and with
    // all-equal areas it degenerates to "first entry in the array" — usually
    // _ts/_t, i.e. a blurry thumb regardless of the element size. Clone the
    // entries first so shared references in project.photos aren't mutated
    // from the render path.
    let dimensionedSizes = allSizes.map((size: any) => this.clonePhotoLike(size));
    const repairedCount = normalizePhotoSizeDimensions(dimensionedSizes, photo.width, photo.height);
    if (repairedCount > 0) {
      console.log(`[CachedRenderer] Photo ${md5.substring(0, 8)}: repaired dimensions on ${repairedCount} size entr${repairedCount === 1 ? 'y' : 'ies'}`);
    }

    // Un-rescue: rescued oversize entries (original/_e admitted for a photo
    // whose bounded ladder was gone) are a last resort — the moment a decent
    // bounded variant (m/b/l) is available again, drop them so the bounded
    // cap policy resumes and the change persists on the next save.
    const hasDecentBounded = dimensionedSizes.some((s: any) => {
      const v = this.getSizeVariant(s);
      return (v === 'm' || v === 'b' || v === 'l') && !(s as any).rescuedOversize;
    });
    if (hasDecentBounded && dimensionedSizes.some((s: any) => (s as any).rescuedOversize)) {
      dimensionedSizes = dimensionedSizes.filter((s: any) => !(s as any).rescuedOversize);
      console.log(`[CachedRenderer] Photo ${md5.substring(0, 8)}: dropped rescued oversize entries — bounded ladder is healthy again`);
    }

    // Truncated-ladder healing (a session that hit transient storage errors
    // strips sizes via loadWithSizeFallback and a save persists the amputated
    // array — e.g. a photo left with ONLY _t, rendering blurry forever):
    // candidate sibling URLs are derived from a surviving variant and
    // VERIFIED in the background — only variants that actually load get
    // restored into the map, so a bad guess can never blank the render.
    this.scheduleLadderVerification(photo, dimensionedSizes, md5);

    const limitedSizes = this.filterBoundedSizes(dimensionedSizes);
    const preferredSource = this.pickBestBoundedSize(limitedSizes);
    const fallbackUrl = preferredSource?.url || this.resolveBoundedFallbackUrl(photo);
    const fallbackThumb = this.pickSmallestBoundedSize(limitedSizes)?.url || photo.thumbnail;

    const nextPhoto: any = this.clonePhotoLike(photo);
    nextPhoto.sizes = limitedSizes;

    if (fallbackUrl) {
      nextPhoto.url = fallbackUrl;
    } else if (limitedSizes.length === 0) {
      // Hard-block original URL fallback: force renderer to use thumbnail/shared blob branches.
      nextPhoto.url = undefined;
    }

    if (fallbackThumb) {
      nextPhoto.thumbnail = fallbackThumb;
    }

    this.ensureUploadedState(nextPhoto);

    if (!this.hasPhotoSourceChanged(photo, nextPhoto)) {
      return;
    }

    this.photoBlobCache.clearPhoto(md5);
    projectPhotosMap?.set(md5, nextPhoto);

    console.log(
      `[CachedRenderer] Photo ${md5.substring(0, 8)} sanitized: sizes=${limitedSizes.length}, url=${this.getSizeVariant({ url: nextPhoto.url }) || 'none'}`
    );
  }

  /**
   * Last ladder-verification attempt per md5. A retry COOLDOWN (not a
   * permanent lock): one round of transient failures (offline blip, storage
   * 5xx) must not disable healing for the whole session.
   */
  private readonly ladderAttemptAt = new Map<string, number>();
  private static readonly LADDER_RETRY_COOLDOWN_MS = 5 * 60 * 1000;

  /**
   * Background truncated-ladder healing: derive candidate URLs for missing
   * variants from a surviving suffixed entry, preflight each one with the
   * same crossOrigin the renderer uses, and restore into the photos map ONLY
   * the ones that actually load. The current render keeps whatever the photo
   * has today (worst case the blurry _t) — the upgrade applies from the next
   * render of that photo onward. A wrong guess can never blank the page.
   */
  private scheduleLadderVerification(photo: any, sizes: any[], md5: string): void {
    const lastAttempt = this.ladderAttemptAt.get(md5);
    if (lastAttempt !== undefined && Date.now() - lastAttempt < CachedRendererService.LADDER_RETRY_COOLDOWN_MS) return;
    if (!this.getUploadedState(photo)) return;

    const origW = Number(photo?.width) || 0;
    const origH = Number(photo?.height) || 0;
    const origEdge = Math.max(origW, origH);
    if (origEdge <= 0) return;

    // Template URL to derive sibling variant URLs from: prefer a surviving
    // suffixed size entry; for fully-amputated ladders (sizes emptied by a
    // bad session and persisted) fall back to the photo's own url — the
    // worst-damaged photos are exactly the ones that need healing most.
    const template = sizes.find((s: any) => {
      if (!s?.url || s.url.startsWith('blob:') || s.url.startsWith('data:')) return false;
      return !!this.getSizeVariant(s);
    });
    let templateUrl: string | undefined = template?.url;
    if (!templateUrl && photo.url && !photo.url.startsWith('blob:') && !photo.url.startsWith('data:')) {
      // Normalize to a suffixed form so swap/strip helpers work uniformly.
      templateUrl = this.getSizeVariant({ url: photo.url })
        ? photo.url
        : this.buildVariantUrl(photo.url, 'b');
    }
    if (!templateUrl) return;

    const present = new Set<string>(
      sizes.map((s: any) => this.getSizeVariant(s)).filter((v): v is Exclude<ReturnType<CachedRendererService['getSizeVariant']>, null> => !!v)
    );

    const candidates: { variant: string; url: string; width: number; height: number; rescued?: boolean }[] = [];
    for (const [variant, maxDim] of Object.entries(PHOTO_VARIANT_MAX_DIM)) {
      if (variant === 'e') continue; // originals stay excluded from selection
      if (present.has(variant)) continue;
      if (maxDim >= origEdge) continue; // server never upscales

      const url = this.swapVariantInUrl(templateUrl, variant);
      if (!url || this.failedVariantUrls.has(this.normalizeUrl(url))) continue;

      const scale = maxDim / origEdge;
      candidates.push({
        variant,
        url,
        width: Math.max(1, Math.round(origW * scale)),
        height: Math.max(1, Math.round(origH * scale))
      });
    }

    // Rescue branch — ONLY when no decent bounded variant survives (nothing
    // at _m or above): try the ORIGINAL/base file and the _e variant as a
    // last resort. A photo with a healthy _m/_b/_l must never get an
    // oversize entry admitted past the bounded-cap policy. Entries are
    // marked so filterBoundedSizes can recognize them.
    const hasDecentBounded = [...present].some(v => v === 'm' || v === 'b' || v === 'l');
    if (!hasDecentBounded) {
      const eUrl = this.swapVariantInUrl(templateUrl, 'e');
      if (eUrl && !this.failedVariantUrls.has(this.normalizeUrl(eUrl))) {
        const scale = Math.min(1, (PHOTO_VARIANT_MAX_DIM['e'] || 2000) / origEdge);
        candidates.push({
          variant: 'e',
          url: eUrl,
          width: Math.max(1, Math.round(origW * scale)),
          height: Math.max(1, Math.round(origH * scale)),
          rescued: true
        });
      }
      const baseUrl = this.stripVariantFromUrl(templateUrl);
      if (baseUrl && !this.failedVariantUrls.has(this.normalizeUrl(baseUrl))) {
        candidates.push({
          variant: 'original',
          url: baseUrl,
          width: origW,
          height: origH,
          rescued: true
        });
      }
    }

    if (candidates.length === 0) return;
    this.ladderAttemptAt.set(md5, Date.now());

    Promise.allSettled(candidates.map(c => this.preflightImage(c.url))).then(results => {
      const verified: typeof candidates = [];
      candidates.forEach((c, i) => {
        const ok = results[i].status === 'fulfilled' && (results[i] as PromiseFulfilledResult<boolean>).value;
        if (ok) {
          verified.push(c);
        } else {
          // Direct evidence of failure — blacklist so retries and twins skip it.
          this.failedVariantUrls.add(this.normalizeUrl(c.url));
        }
      });
      if (verified.length === 0) {
        console.log(`[CachedRenderer] Photo ${md5.substring(0, 8)}: no missing size variants could be verified`);
        return;
      }

      const projectPhotosMap = (this as any).projectPhotosMap as Map<string, any> | undefined;
      if (!projectPhotosMap) return;

      // When any bounded variant verified, drop the rescued oversize entries —
      // they are a last resort, not a preference.
      const boundedVerified = verified.filter(c => !c.rescued);
      const usable = boundedVerified.length > 0 ? boundedVerified : verified;

      // Apply to the photo AND its background twin (user-photo backgrounds
      // live under a separate 'bg_<md5>' map entry copied from the original —
      // healing only one of them leaves the other rendering _t until the
      // project is saved and reloaded).
      const twinMd5 = md5.startsWith('bg_') ? md5.substring(3) : `bg_${md5}`;
      for (const targetMd5 of [md5, twinMd5]) {
        const mapPhoto = projectPhotosMap.get(targetMd5);
        if (!mapPhoto) continue;

        const currentUrls = new Set((mapPhoto.sizes || []).map((s: any) => this.normalizeUrl(s?.url)));
        const additions = usable
          .filter(c => !currentUrls.has(this.normalizeUrl(c.url)))
          .map(c => {
            const entry: any = { name: '', width: c.width, height: c.height, url: c.url };
            if (c.rescued) entry.rescuedOversize = true;
            return entry;
          });
        if (additions.length === 0) continue;

        const nextPhoto = this.clonePhotoLike(mapPhoto);
        nextPhoto.sizes = [...(mapPhoto.sizes || []), ...additions];
        projectPhotosMap.set(targetMd5, nextPhoto);
        this.photoBlobCache.clearPhoto(targetMd5);
        this.ladderAttemptAt.set(targetMd5, Date.now());

        console.log(`[CachedRenderer] Photo ${targetMd5.substring(0, 12)}: verified & restored ${additions.length} missing size variant(s) (${verified.map(v => v.variant).join(',')})`);
      }
    });
  }

  /**
   * Load-test a URL exactly like the renderer will: crossOrigin canvas load
   * WITH the renderer's cacheKey query — the real load appends it, and a
   * bare-URL preflight could hit a CORS-poisoned plain-cache entry (this
   * repo's documented recurring pitfall) and mispredict the outcome.
   */
  private preflightImage(url: string): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      const timeout = setTimeout(() => resolve(false), 8000);
      img.onload = () => {
        clearTimeout(timeout);
        resolve(img.naturalWidth > 0);
      };
      img.onerror = () => {
        clearTimeout(timeout);
        resolve(false);
      };
      const cacheKey = (this as any).cacheKey;
      img.src = cacheKey ? `${url}${url.includes('?') ? '&' : '?'}cacheKey=${cacheKey}` : url;
    });
  }

  /** Replace the variant suffix in a sized URL (…_t.jpg → …_b.jpg). */
  private swapVariantInUrl(url: string, variant: string): string | undefined {
    if (!url) return undefined;
    const swapped = url.replace(
      /_(ts|t|s|m|b|l|e)(\.[a-z0-9]+)((?:\?|#).*)?$/i,
      `_${variant}$2$3`
    );
    return swapped !== url ? swapped : undefined;
  }

  /** Remove the variant suffix from a sized URL (…_t.jpg → ….jpg). */
  private stripVariantFromUrl(url: string): string | undefined {
    if (!url) return undefined;
    const stripped = url.replace(
      /_(ts|t|s|m|b|l|e)(\.[a-z0-9]+)((?:\?|#).*)?$/i,
      `$2$3`
    );
    return stripped !== url ? stripped : undefined;
  }

  private getSizeArea(size: any): number {
    return (Number(size?.width) || 0) * (Number(size?.height) || 0);
  }

  private getSizeVariant(size: any): 'ts' | 't' | 's' | 'm' | 'b' | 'l' | 'e' | null {
    const raw = `${size?.name || ''}|${size?.serverName || ''}|${size?.url || ''}`.toLowerCase();
    const match = raw.match(/(?:^|[_/.-])(ts|t|s|m|b|l|e)(?=(?:\.[a-z0-9]+)?(?:\?|#|$|[|]))/);
    return (match?.[1] as any) ?? null;
  }

  private isBoundedVariant(variant: ReturnType<CachedRendererService['getSizeVariant']>): boolean {
    return variant === 'ts' || variant === 't' || variant === 's' || variant === 'm' || variant === 'b' || variant === 'l';
  }

  private getVariantRank(variant: ReturnType<CachedRendererService['getSizeVariant']>): number {
    switch (variant) {
      case 'l': return 6;
      case 'b': return 5;
      case 'm': return 4;
      case 's': return 3;
      case 't': return 2;
      case 'ts': return 1;
      default: return 0;
    }
  }

  private pickBestBoundedSize(sizes: any[]): any | undefined {
    if (!sizes.length) return undefined;
    return [...sizes].sort((a, b) => {
      const areaDiff = this.getSizeArea(b) - this.getSizeArea(a);
      if (areaDiff !== 0) return areaDiff;
      return this.getVariantRank(this.getSizeVariant(b)) - this.getVariantRank(this.getSizeVariant(a));
    })[0];
  }

  private pickSmallestBoundedSize(sizes: any[]): any | undefined {
    if (!sizes.length) return undefined;
    return [...sizes].sort((a, b) => {
      const areaDiff = this.getSizeArea(a) - this.getSizeArea(b);
      if (areaDiff !== 0) return areaDiff;
      return this.getVariantRank(this.getSizeVariant(a)) - this.getVariantRank(this.getSizeVariant(b));
    })[0];
  }

  private filterBoundedSizes(sizes: any[]): any[] {
    if (!sizes.length) return [];

    // The cap is the largest ALLOWED variant present: _l when the server
    // generated it, otherwise _b.
    const capSize = sizes.find(size => this.getSizeVariant(size) === 'l')
      || sizes.find(size => this.getSizeVariant(size) === 'b');
    const capArea = capSize ? this.getSizeArea(capSize) : 0;

    return sizes.filter(size => {
      const variant = this.getSizeVariant(size);
      const normalizedUrl = this.normalizeUrl(size?.url);
      const area = this.getSizeArea(size);

      if (!normalizedUrl) return false;
      // Verified last-resort entries (original/_e restored for a photo whose
      // bounded ladder is gone from storage) bypass the bounded-cap policy.
      if ((size as any)?.rescuedOversize === true) return true;
      if (variant === 'e') return false;

      if (this.isBoundedVariant(variant)) {
        return capArea <= 0 || area <= 0 || area <= capArea;
      }

      // Unknown variant: only accept if dimensions are clearly bounded.
      const width = Number(size?.width) || 0;
      const height = Number(size?.height) || 0;
      if (capArea > 0) {
        return area > 0 && area <= capArea;
      }
      return width > 0 && height > 0 &&
        width <= this.MAX_BOUNDED_EDGE &&
        height <= this.MAX_BOUNDED_EDGE;
    });
  }

  private resolveBoundedFallbackUrl(photo: any): string | undefined {
    if (this.isBoundedImageUrl(photo?.thumbnail)) {
      return photo.thumbnail;
    }

    if (this.isBoundedImageUrl(photo?.url)) {
      return photo.url;
    }

    const fromServerName = this.buildVariantUrlFromServerName(photo?.url, photo?.serverName, 'b');
    if (fromServerName) {
      return fromServerName;
    }

    const fromUrl = this.buildVariantUrl(photo?.url, 'b');
    if (fromUrl) {
      return fromUrl;
    }

    return undefined;
  }

  private hasPhotoSourceChanged(currentPhoto: any, nextPhoto: any): boolean {
    if (this.normalizeUrl(currentPhoto?.url) !== this.normalizeUrl(nextPhoto?.url)) return true;
    if (this.normalizeUrl(currentPhoto?.thumbnail) !== this.normalizeUrl(nextPhoto?.thumbnail)) return true;
    if (this.getUploadedState(currentPhoto) !== this.getUploadedState(nextPhoto)) return true;
    return !this.areSameSizeLists(currentPhoto?.sizes, nextPhoto?.sizes);
  }

  private areSameSizeLists(currentSizes: any, nextSizes: any): boolean {
    const current = Array.isArray(currentSizes) ? currentSizes : [];
    const next = Array.isArray(nextSizes) ? nextSizes : [];
    if (current.length !== next.length) return false;

    for (let i = 0; i < current.length; i++) {
      const c = current[i];
      const n = next[i];
      if (this.normalizeUrl(c?.url) !== this.normalizeUrl(n?.url)) return false;
      if ((Number(c?.width) || 0) !== (Number(n?.width) || 0)) return false;
      if ((Number(c?.height) || 0) !== (Number(n?.height) || 0)) return false;
      if ((c?.name || '') !== (n?.name || '')) return false;
    }

    return true;
  }

  private isBoundedImageUrl(url?: string): boolean {
    if (!url) return false;
    if (url.startsWith('blob:') || url.startsWith('data:')) return true;
    const variant = this.getSizeVariant({ url });
    return this.isBoundedVariant(variant);
  }

  private buildVariantUrlFromServerName(url: string | undefined, serverName: string | undefined, variant: 'b'): string | undefined {
    if (!url || !serverName) return undefined;
    const match = serverName.match(/^(.*?)(\.[^./?#]+)$/);
    if (!match) return undefined;
    const variantName = `${match[1]}_${variant}${match[2]}`;
    if (!url.includes(serverName)) return undefined;
    return url.replace(serverName, variantName);
  }

  private buildVariantUrl(url?: string, variant: 'b' = 'b'): string | undefined {
    if (!url || url.startsWith('blob:') || url.startsWith('data:')) return undefined;

    // Already bounded.
    if (this.isBoundedImageUrl(url)) return url;

    const splitIndexQuestion = url.indexOf('?');
    const splitIndexHash = url.indexOf('#');
    const splitIndex = splitIndexQuestion === -1
      ? splitIndexHash
      : (splitIndexHash === -1 ? splitIndexQuestion : Math.min(splitIndexQuestion, splitIndexHash));

    const rawPath = splitIndex >= 0 ? url.slice(0, splitIndex) : url;
    const suffix = splitIndex >= 0 ? url.slice(splitIndex) : '';

    const extMatch = rawPath.match(/^(.*?)(\.[^./]+)$/);
    if (!extMatch) return undefined;

    const name = extMatch[1];
    const ext = extMatch[2];
    const replacedName = name.replace(/_(ts|t|s|m|b|l|e)$/i, '');
    return `${replacedName}_${variant}${ext}${suffix}`;
  }

  private normalizeUrl(url?: string): string {
    if (!url) return '';
    return url.split('?')[0].split('#')[0];
  }

  private getScaledRequest(
    width?: number,
    height?: number,
    crop?: Crop
  ): { width?: number; height?: number; crop?: Crop } {
    const scale = this.normalizeScale(this.presentationScale);
    if (scale === 1) {
      return { width, height, crop };
    }

    const scaledWidth = this.scaleDimension(width, scale);
    const scaledHeight = this.scaleDimension(height, scale);
    const scaledCrop = this.scaleCropForRequest(crop, scale);

    return {
      width: scaledWidth,
      height: scaledHeight,
      crop: scaledCrop
    };
  }

  private scaleCropForRequest(crop?: Crop, scale = 1): Crop | undefined {
    if (!crop) return undefined;
    const clone = this.clonePhotoLike(crop);
    clone.width = this.scaleDimension(crop.width, scale) || crop.width;
    clone.height = this.scaleDimension(crop.height, scale) || crop.height;
    return clone;
  }

  private scaleDimension(value?: number, scale = 1): number | undefined {
    if (value == null || !Number.isFinite(value)) return value;
    return Math.max(1, Math.round(value * scale));
  }

  private normalizeScale(scale: number): number {
    if (!Number.isFinite(scale) || scale <= 0) return 1;
    // Avoid extreme over/under-scaling in request calculations.
    return Math.min(2, Math.max(0.05, scale));
  }

  private clonePhotoLike(photo: any): any {
    const prototype = Object.getPrototypeOf(photo) || Object.prototype;
    const clone = Object.create(prototype);
    Object.assign(clone, photo);
    return clone;
  }

  private getUploadedState(photo: any): boolean {
    if (!photo) return false;
    try {
      if (typeof photo.uploaded === 'boolean') {
        return photo.uploaded;
      }
      return Number(photo.id) > 0 && photo.url !== undefined;
    } catch {
      return Number(photo.id) > 0 && photo.url !== undefined;
    }
  }

  private ensureUploadedState(photo: any): void {
    const proto = Object.getPrototypeOf(photo);
    const descriptor = proto ? Object.getOwnPropertyDescriptor(proto, 'uploaded') : undefined;
    if (descriptor?.get && !descriptor?.set) {
      // Getter-only model (PhotoInfo class) – uploaded is derived from id/url.
      return;
    }
    photo.uploaded = Number(photo.id) > 0 && photo.url !== undefined;
  }

}
